import { BUTTON_TYPE, DSButton, DSModal } from '@repo/design-system/index'
import { QIcon_calendar_plus } from '@repo/icons/index'
import classNames from 'classnames'
import { format, getDate } from 'date-fns'
import { useCallback, useEffect, useState } from 'react'
import { n_caf } from '../../../../../modules/i18n/strings/auto/wording_webview'
import useTranslation from '../../../../../modules/i18n/useTranslation'
import { COLORS } from '../../../../../modules/vars'
import Calendar, { DATE_POSITION } from './Calendar'

export interface ITimePeriodOption {
  value: number
  name: string
}

interface ITimePeriodPicker {
  currentTimePeriod: number
  start: Date
  end: Date
  onClickSearch: (selectedTimePeriod: number, startDate?: Date, endDate?: Date) => void
}

const CalendarModal = ({
  showPopup,
  setShowPopup,
  startDate,
  endDate,
  position,
  setDate,
  setUpdateBoth,
  handleDate,
}) => {
  const { t } = useTranslation()

  return (
    <DSModal id="TextOnlySMOneButtonPopup" isOpen={showPopup} setIsOpen={setShowPopup} size={'sm'}>
      <DSModal.Content>
        <Calendar
          setDate={setDate}
          currentDate={position === DATE_POSITION.START ? startDate : endDate}
          position={position}
          dateToCompare={position === DATE_POSITION.START ? endDate : startDate}
          setUpdateBoth={setUpdateBoth}
        />
      </DSModal.Content>
      <DSModal.ButtonContainer direction="horizontal">
        <DSButton onClick={() => setShowPopup(false)} type={BUTTON_TYPE.OUTLINE} customBgColor={COLORS.WHITE}>
          <DSButton.Label>{t(n_caf.statistic.bottomsheet_cancel)}</DSButton.Label>
        </DSButton>
        <DSButton onClick={() => handleDate(position)}>
          <DSButton.Label>{t(n_caf.statistic.bottomsheet_apply)}</DSButton.Label>
        </DSButton>
      </DSModal.ButtonContainer>
    </DSModal>
  )
}

const TimePeriodPicker = ({ currentTimePeriod, start, end, onClickSearch }: ITimePeriodPicker) => {
  const { t } = useTranslation()
  const [startDate, setStartDate] = useState<Date>(start)
  const [showPopup, setShowPopup] = useState<boolean>(false)
  const [endDate, setEndDate] = useState<Date>(end)
  const [date, setDate] = useState<Date>(null)
  const [updateDate, setUpdateDate] = useState<string>(null)
  const [updateBoth, setUpdateBoth] = useState(false)
  const [currentPosition, setCurrentPosition] = useState<string>(null)

  const handleOpenCalendar = (position: string) => {
    setDate(position === DATE_POSITION.START ? startDate : endDate)
    setCurrentPosition(position)
    setShowPopup(true)
  }

  const timePeriodList: ITimePeriodOption[] = [
    { value: 1, name: t(n_caf.statistic.date_chip1) },
    { value: 3, name: t(n_caf.statistic.date_chip2) },
    { value: 6, name: t(n_caf.statistic.date_chip3) },
    { value: 12, name: t(n_caf.statistic.date_chip4) },
  ]
  const [selectedTimePeriod, setSelectedTimePeriod] = useState<number>(currentTimePeriod)
  const isSelectedTimePeriod = useCallback(
    (timePeriodData) => selectedTimePeriod === timePeriodData,
    [selectedTimePeriod]
  )

  const handleDate = (position: string) => {
    setUpdateDate(position)
    setSelectedTimePeriod(0)
    setCurrentPosition(position)
    setShowPopup(false)
  }

  useEffect(() => {
    if (updateDate === DATE_POSITION.START) {
      setStartDate(date)
      if (updateBoth) {
        setEndDate(date)
      }
      setUpdateDate(null)
    } else if (updateDate === DATE_POSITION.END) {
      setEndDate(date)
      if (updateBoth) {
        setStartDate(date)
      }
      setUpdateDate(null)
    }
  }, [updateDate, updateBoth, date])
  // const monthForSafari = (m) => (m < 10 ? '0' + m : m)
  return (
    <div className={styles.wrapper}>
      <h3 className={styles.title}>{t(n_caf.statistic.date_title)}</h3>
      <div className={styles.timePeriodContainer.box}>
        {timePeriodList?.map((timePeriodItem, idx) => (
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          <div
            key={'timePeriod' + idx}
            className={styles.filters.container(isSelectedTimePeriod(timePeriodItem.value))}
            onClick={() => setSelectedTimePeriod(timePeriodItem.value)}
          >
            <span className={styles.filters.text(isSelectedTimePeriod(timePeriodItem.value))}>
              {timePeriodItem.name}
            </span>
          </div>
        ))}
      </div>
      <div className={styles.calendarBtn.container(!selectedTimePeriod)}>
        <div className={styles.calendarBtn.box} onClick={() => handleOpenCalendar(DATE_POSITION.START)}>
          <div className={styles.calendarBtn.text(!selectedTimePeriod)} key={getDate(startDate)}>
            {format(startDate, 'yyyy.MM.dd')}
          </div>
          {!selectedTimePeriod ? (
            <QIcon_calendar_plus color={COLORS.PURPLE500} />
          ) : (
            <QIcon_calendar_plus color={COLORS.GRAY500} />
          )}
        </div>
        <div className={styles.calendarBtn.dash(!selectedTimePeriod)}></div>
        <div className={styles.calendarBtn.box} onClick={() => handleOpenCalendar(DATE_POSITION.END)}>
          <div className={styles.calendarBtn.text(!selectedTimePeriod)} key={getDate(endDate)}>
            {format(endDate, 'yyyy.MM.dd')}
          </div>
          {!selectedTimePeriod ? (
            <QIcon_calendar_plus color={COLORS.PURPLE500} />
          ) : (
            <QIcon_calendar_plus color={COLORS.GRAY500} />
          )}
        </div>
      </div>
      <button
        className={styles.searchBtn.container}
        onClick={() => onClickSearch(selectedTimePeriod, startDate, endDate)}
      >
        <div className={styles.searchBtn.text}>{t(n_caf.statistic.date_btn)}</div>
      </button>
      <CalendarModal
        showPopup={showPopup}
        setShowPopup={setShowPopup}
        startDate={startDate}
        endDate={endDate}
        position={currentPosition}
        setDate={setDate}
        setUpdateBoth={setUpdateBoth}
        handleDate={handleDate}
      />
    </div>
  )
}

export default TimePeriodPicker

const styles = {
  wrapper: `px-[20px] pt-[16px] pb-[31px]`,
  title: `text-h2 pb-[24px] text-left font-bold text-black`,
  timePeriodContainer: {
    box: `flex gap-[8px]`,
  },
  filters: {
    container: (active?: boolean) =>
      classNames(
        `text-h5 flex-shrink-0 rounded-full border py-[8px] px-[12px] font-medium capitalize leading-[18px] tracking-tight`,
        active
          ? `border-purple-500 bg-purple-50 text-purple-500`
          : `border-gray-100 bg-white bg-opacity-20 text-gray-500`
      ),
    text: (active?: boolean) => classNames(`text-h5 font-medium`, active ? `text-purple-500` : `text-gray-500`),
  },
  searchBtn: {
    container: `mt-[32px] h-[56px] w-full rounded-md bg-purple-500`,
    text: `text-[14px] text-white`,
  },
  calendarBtn: {
    container: (active?: boolean) =>
      classNames(
        `mt-[18px] flex h-[38px] items-center justify-between rounded-md border px-[6px] py-[8px]`,
        active ? `border-purple-500` : `border-gray-200`
      ),
    dash: (active?: boolean) => classNames(`h-[1px] w-[12px]`, active ? `bg-purple-500` : `bg-gray-500`),
    text: (active?: boolean) => classNames(`text-[14px] font-medium`, active ? `text-purple-500` : `text-gray-500`),
    box: `flex w-full items-center justify-between px-[6px]`,
  },
}
